import { MessageSquare, Send, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { trackEvent } from "./analytics";
import "./chat-widget.css";

type ChatMessage = {
  role: "user" | "assistant";
  content: string;
  seed?: boolean; // wiadomość lokalna (powitanie) — nie wysyłana do API
  pending?: boolean; // odpowiedź w trakcie strumieniowania
};

const STORAGE_KEY = "ochro-chat-v1";
const SEEN_KEY = "ochro-chat-seen";
const SEEN_CALC_KEY = "ochro-chat-seen-calc";
const COOKIE_KEY = "ochro-cookie-consent";
const CID_KEY = "ochro-chat-cid";

const GREETING =
  "Hej! 👋 Jestem wirtualnym asystentem Ochro. Pytaj śmiało o ubezpieczenia — na życie, zdrowotne, pod kredyt czy oszczędnościowe. W czym mogę pomóc?";

const TEASER_TEXT = "Cześć! 👋 W czym mogę pomóc?";
const CALC_TEASER_TEXT = "Pytanie o wynik? Chętnie pomogę.";

const SUGGESTIONS = [
  "Czym różni się ochronne od oszczędnościowego?",
  "Ile kosztuje ubezpieczenie na życie?",
  "Jakie ubezpieczenie pod kredyt hipoteczny?",
  "Co to leczenie za granicą?"
];

const ERROR_TEXT =
  "Przepraszam, chwilowo nie mogę odpowiedzieć. Spróbuj ponownie za chwilę lub zadzwoń do doradcy: 510 509 019.";

function isOnScreen(selector: string) {
  const el = document.querySelector<HTMLElement>(selector);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.bottom > 0 && rect.top < window.innerHeight;
}

function shouldSuppressTeaser() {
  const path = window.location.pathname;
  return (
    path.includes("/kalkulator-") ||
    path === "/kontakt/" ||
    window.location.hash === "#formularz" ||
    Boolean(document.querySelector(".calculator-shell"))
  );
}

function escapeHtml(value: string) {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildLink(url: string, label: string) {
  const external = /^https?:/i.test(url);
  const safe = url.replace(/"/g, "%22");
  const attrs = external ? ' target="_blank" rel="noopener noreferrer"' : "";
  return `<a href="${safe}"${attrs}>${label}</a>`;
}

// Bezpieczne renderowanie: najpierw escape, potem proste markdown (linki, pogrubienia, listy).
function renderRich(text: string) {
  const inline = (segment: string) =>
    segment
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, (_m, label, url) => buildLink(url, label))
      .replace(
        /(^|[\s(])((?:https?:\/\/)[^\s)]+)(?=$|[\s).,!?])/g,
        (_m, pre, url) => `${pre}${buildLink(url, url)}`
      );

  const lines = escapeHtml(text).split(/\r?\n/);
  let html = "";
  let inList = false;
  for (const line of lines) {
    const bullet = line.match(/^\s*[-•]\s+(.*)$/);
    if (bullet) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      html += `<li>${inline(bullet[1])}</li>`;
      continue;
    }
    if (inList) {
      html += "</ul>";
      inList = false;
    }
    if (line.trim()) {
      html += `<p>${inline(line)}</p>`;
    }
  }
  if (inList) {
    html += "</ul>";
  }
  return html;
}

function loadStored(): ChatMessage[] | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length) return parsed as ChatMessage[];
  } catch {
    /* ignore */
  }
  return null;
}

const seedMessages = (): ChatMessage[] => [{ role: "assistant", content: GREETING, seed: true }];

// Stały identyfikator rozmowy w obrębie sesji (do łączenia wiadomości w panelu admina).
function getConversationId(): string {
  const fresh = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  try {
    let id = sessionStorage.getItem(CID_KEY);
    if (!id) {
      id = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : fresh();
      sessionStorage.setItem(CID_KEY, id);
    }
    return id;
  } catch {
    return fresh();
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [teaser, setTeaser] = useState(false);
  const [teaserText, setTeaserText] = useState(TEASER_TEXT);
  const [teaserSuppressed, setTeaserSuppressed] = useState(false);
  const [cookieReady, setCookieReady] = useState(false);
  const [bannerOffset, setBannerOffset] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>(seedMessages);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const openRef = useRef(false);
  const teaserTextRef = useRef(TEASER_TEXT);
  const calcTeaserTimerRef = useRef(0);

  const scheduleCalcTeaser = (delay = 2600, attempt = 0) => {
    window.clearTimeout(calcTeaserTimerRef.current);
    calcTeaserTimerRef.current = window.setTimeout(() => {
      if (openRef.current) return;
      // Nie zasłaniaj szybkiego formularza: dymek ma pomagać tym, którzy odjechali
      // od wyniku, a nie konkurować z polem telefonu tuż pod kwotą.
      if (isOnScreen(".quick-lead-form")) {
        if (attempt < 4) scheduleCalcTeaser(8000, attempt + 1);
        return;
      }
      teaserTextRef.current = CALC_TEASER_TEXT;
      setTeaserText(CALC_TEASER_TEXT);
      setTeaser(true);
      trackEvent("chat_teaser_shown", { source: "calc" });
    }, delay);
  };

  // "Widziane" zapisujemy dopiero przy interakcji (zamknięcie/otwarcie), nie przy samym
  // pokazaniu — inaczej teaser po kalkulacji miał dokładnie jedną szansę na sesję.
  const markSeen = () => {
    try {
      sessionStorage.setItem(SEEN_KEY, "1");
      if (teaserTextRef.current === CALC_TEASER_TEXT) {
        sessionStorage.setItem(SEEN_CALC_KEY, "1");
      }
    } catch {
      /* ignore */
    }
  };

  // Odtwórz rozmowę z sessionStorage i wykryj decyzję cookies. Launcher jest widoczny
  // zawsze; decyzja cookies (cookieReady) wstrzymuje tylko proaktywne teasery,
  // żeby nie nakładały się na otwarty baner.
  useEffect(() => {
    const stored = loadStored();
    if (stored) {
      setMessages(stored.map((m) => ({ ...m, pending: false })));
    }
    const checkCookie = () => {
      try {
        if (window.localStorage.getItem(COOKIE_KEY)) setCookieReady(true);
      } catch {
        setCookieReady(true); // tryb prywatny — nie blokuj widgetu
      }
    };
    checkCookie();
    window.addEventListener("ochro-cookie-consent", checkCookie);
    window.addEventListener("storage", checkCookie);
    return () => {
      window.removeEventListener("ochro-cookie-consent", checkCookie);
      window.removeEventListener("storage", checkCookie);
    };
  }, []);

  useEffect(() => {
    const sync = () => {
      const suppressed = shouldSuppressTeaser();
      setTeaserSuppressed(suppressed);
      if (suppressed) setTeaser(false);
    };
    sync();
    window.addEventListener("hashchange", sync);
    window.addEventListener("popstate", sync);
    return () => {
      window.removeEventListener("hashchange", sync);
      window.removeEventListener("popstate", sync);
    };
  }, []);

  // Dopóki baner cookies czeka na decyzję, unieś launcher ponad baner,
  // żeby nie zasłaniał jego przycisków (i sam nie znikał pod banerem).
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // Unieś widget nad baner cookies (dopóki brak decyzji) i nad sticky pasek CTA
  // kalkulatora; przy otwartym panelu wracamy do dołu, żeby panel nie wystawał
  // górą poza viewport na małych ekranach.
  useEffect(() => {
    const update = () => {
      if (openRef.current) {
        setBannerOffset(0);
        return;
      }
      let offset = 0;
      if (!cookieReady) {
        const banner = document.querySelector<HTMLElement>(".cookie-banner");
        if (banner) offset = Math.max(offset, banner.offsetHeight + 28);
      }
      const sticky = document.querySelector<HTMLElement>(".calc-sticky-cta");
      if (sticky && sticky.offsetHeight > 0) {
        offset = Math.max(offset, sticky.offsetHeight + 12);
      }
      setBannerOffset(offset);
    };
    update();
    const lateMount = window.setTimeout(update, 600);
    window.addEventListener("resize", update);
    window.addEventListener("ochro:layout", update);
    return () => {
      window.clearTimeout(lateMount);
      window.removeEventListener("resize", update);
      window.removeEventListener("ochro:layout", update);
    };
  }, [cookieReady, open]);

  // Proaktywna zachęta po krótkiej chwili od wejścia (raz na sesję). Celowo NIE czeka
  // na decyzję cookies — większość użytkowników mobile ignoruje baner, a launcher
  // i teaser są uniesione nad baner przez bannerOffset. Sam czat też działa bez zgody.
  useEffect(() => {
    if (teaserSuppressed) return;
    let alreadySeen = false;
    try {
      alreadySeen = sessionStorage.getItem(SEEN_KEY) === "1";
    } catch {
      /* ignore */
    }
    if (alreadySeen) return;
    const id = window.setTimeout(() => {
      teaserTextRef.current = TEASER_TEXT;
      setTeaser(true);
      trackEvent("chat_teaser_shown", { source: "general" });
    }, 3500);
    return () => window.clearTimeout(id);
  }, [teaserSuppressed]);

  // Na stronach z kalkulatorem teaser pojawia się po obliczeniu składki
  // („Pytanie o wynik?") — bez czekania na decyzję cookies.
  useEffect(() => {
    const onAnalytics = (event: Event) => {
      const name = (event as CustomEvent<{ name?: string }>).detail?.name;
      if (name !== "oblicz_skladke" || openRef.current) return;
      try {
        if (sessionStorage.getItem(SEEN_CALC_KEY) === "1") return;
      } catch {
        /* ignore */
      }
      scheduleCalcTeaser();
    };
    window.addEventListener("ochro:analytics", onAnalytics);
    return () => {
      window.clearTimeout(calcTeaserTimerRef.current);
      window.removeEventListener("ochro:analytics", onAnalytics);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages.filter((m) => !m.pending)));
    } catch {
      /* ignore */
    }
  }, [messages]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) {
      setTeaser(false);
      markSeen();
      const id = window.setTimeout(() => inputRef.current?.focus(), 120);
      return () => window.clearTimeout(id);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const updateLastAssistant = (content: string, pending: boolean) => {
    setMessages((prev) => {
      const copy = [...prev];
      const i = copy.length - 1;
      if (i >= 0 && copy[i].role === "assistant") {
        copy[i] = { ...copy[i], content, pending };
      }
      return copy;
    });
  };

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;

    const history = [...messages, { role: "user" as const, content: text }];
    setMessages([...history, { role: "assistant", content: "", pending: true }]);
    trackEvent("chat_message_sent");
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    setBusy(true);

    const payload = history
      .filter((m) => !m.seed && m.content.trim())
      .map((m) => ({ role: m.role, content: m.content }));

    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ messages: payload, conversationId: getConversationId() }),
        signal: controller.signal
      });

      const ctype = res.headers.get("content-type") || "";
      if (!res.ok || !res.body || ctype.includes("application/json")) {
        updateLastAssistant(ERROR_TEXT, false);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let nl: number;
        while ((nl = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, nl).trim();
          buffer = buffer.slice(nl + 1);
          if (!line || line.startsWith(":") || !line.startsWith("data:")) continue;
          const data = line.slice(5).trim();
          if (data === "[DONE]") continue;
          try {
            const json = JSON.parse(data);
            const delta = json?.choices?.[0]?.delta?.content;
            if (typeof delta === "string" && delta) {
              acc += delta;
              updateLastAssistant(acc, true);
            }
          } catch {
            /* pomijaj nieparsowalne fragmenty */
          }
        }
      }

      updateLastAssistant(
        acc.trim() || "Nie udało się wygenerować odpowiedzi. Spróbuj zapytać inaczej lub zadzwoń: 510 509 019.",
        false
      );
    } catch (err) {
      if (!controller.signal.aborted) {
        updateLastAssistant(ERROR_TEXT, false);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const onInputKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void send(input);
    }
  };

  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  };

  const showSuggestions = messages.filter((m) => !m.seed).length === 0 && !busy;

  return (
    <div className="ochro-chat" style={bannerOffset ? { bottom: `${bannerOffset}px` } : undefined}>
      {open && (
        <div className="ochro-chat-panel" role="dialog" aria-label="Wirtualny Asystent" aria-modal="false">
          <div className="ochro-chat-head">
            <span className="ochro-chat-avatar" aria-hidden="true">
              <Sparkles size={20} />
            </span>
            <div className="ochro-chat-head-text">
              <strong>Wirtualny Asystent</strong>
              <span>Zwykle odpowiada od razu</span>
            </div>
            <button type="button" className="ochro-chat-close" onClick={() => setOpen(false)} aria-label="Zamknij czat">
              <X size={18} />
            </button>
          </div>

          <div className="ochro-chat-body" ref={bodyRef}>
            {messages.map((msg, i) => (
              <div key={i} className={`ochro-chat-msg ${msg.role}`}>
                {msg.pending && !msg.content ? (
                  <span className="ochro-chat-typing" aria-label="Asystent pisze">
                    <span />
                    <span />
                    <span />
                  </span>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: renderRich(msg.content) }} />
                )}
              </div>
            ))}

            {showSuggestions && (
              <div className="ochro-chat-suggestions">
                {SUGGESTIONS.map((q) => (
                  <button key={q} type="button" className="ochro-chat-chip" onClick={() => void send(q)}>
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="ochro-chat-foot">
            <div className="ochro-chat-inputrow">
              <textarea
                ref={inputRef}
                className="ochro-chat-input"
                rows={1}
                placeholder="Napisz pytanie…"
                value={input}
                onChange={(e) => {
                  setInput(e.target.value);
                  autoGrow(e.target);
                }}
                onKeyDown={onInputKey}
                aria-label="Treść wiadomości"
              />
              <button
                type="button"
                className="ochro-chat-send"
                onClick={() => void send(input)}
                disabled={busy || !input.trim()}
                aria-label="Wyślij"
              >
                <Send size={18} />
              </button>
            </div>
            <p className="ochro-chat-disclaimer">
              Asystent AI — informacyjnie, to nie jest oferta; rozmowa może być zapisywana. Doradca:{" "}
              <a href="tel:+48510509019">510&nbsp;509&nbsp;019</a>
            </p>
          </div>
        </div>
      )}

      {teaser && !open && (
        <div
          className="ochro-chat-teaser"
          role="button"
          tabIndex={0}
          onClick={() => {
            trackEvent("chat_open", { source: "teaser" });
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              trackEvent("chat_open", { source: "teaser" });
              setOpen(true);
            }
          }}
        >
          <span className="ochro-chat-teaser-avatar" aria-hidden="true">
            <Sparkles size={17} />
          </span>
          <span className="ochro-chat-teaser-text">{teaserText}</span>
          <button
            type="button"
            className="ochro-chat-teaser-close"
            aria-label="Zamknij podpowiedź"
            onClick={(e) => {
              e.stopPropagation();
              setTeaser(false);
              markSeen();
            }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      <button
        type="button"
        className="ochro-chat-launcher"
        onClick={() => {
          if (!openRef.current) trackEvent("chat_open", { source: "launcher" });
          setOpen((v) => !v);
        }}
        aria-label={open ? "Zamknij czat" : "Otwórz czat z Wirtualnym Asystentem"}
        aria-expanded={open}
      >
        <span className="ochro-chat-launcher-icon" aria-hidden="true">
          {open ? <X size={24} /> : <MessageSquare size={24} />}
        </span>
        {!open && <span className="ochro-chat-dot" aria-hidden="true" />}
      </button>
    </div>
  );
}
